/* eslint-disable */
// Copied from `banquet-tools`, which has different eslint rules

import { ApolloLink } from '@apollo/client'
import { ErrorResponse } from '@apollo/client/link/persisted-queries'
import { print } from 'graphql'
import {
  createPersistedQueryLink,
  type PersistedQueryLink
} from '@apollo/client/link/persisted-queries'

/**
 * This is forked from https://github.toasttab.com/toasttab/banquet-tools/blob/88c09b6ce4d4edeb17e624be4f3388f24638acb8/packages/graphql/persisted-queries-link/src/PersistedQueriesLink.ts
 *
 * Difference:
 * - updated to work in an isomorphic environment, i.e. not requiring `localStorage`
 * - does not set the hash header so that `BatchHttpLink` can work correctly
 *   - this requires setting the hash header separately via `customFetch`
 *
 * TODO Backport these changes to `banquet-tools`.
 */
export const createPersistedQueriesLink = (
  opts: Omit<
    PersistedQueryLink.Options & {
      disableErrorLogging?: boolean
    },
    'generateHash' | 'sha256'
  > = {
    disableErrorLogging: false
  }
) => {
  const debugPersistedOperations =
    (typeof localStorage !== 'undefined' &&
      localStorage.getItem('debugPersistedOperations')) ||
    process.env.DEBUG_PERSISTED_OPERATIONS === 'true'

  let devHeaders = {}
  try {
    if (process.env.NODE_ENV === 'development') {
      devHeaders = {
        'Toast-Dev-Allow-Arbitrary-Operations': true
      }
    }
  } catch (e) {
    // Ignore error
  }

  const links = [
    new ApolloLink((operation, forward) => {
      if (!(operation.query as any)?.['__meta__']?.['hash']) {
        if (!opts.disableErrorLogging) {
          console.error('Operation does not have a hash:', operation.query)
        }
        return forward(operation)
      }

      if (debugPersistedOperations) {
        console.log(
          'Persisted hash:',
          (operation.query as any)['__meta__']?.['hash']
        )
        console.log('Persisted operation:', print(operation.query))
        console.log('Persisted operation variables:', operation.variables)
      }

      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          // FIXME: Bypass setting hash header here to make BatchHttpLink work. By default, it
          // uses `batchKey` based on common headers (https://github.com/apollographql/apollo-client/blob/4e2e3e8ed5461a9fb31ba0fa2d17db39fbba564c/src/link/batch-http/batchHttpLink.ts#L239-L253).
          // If we set the hash here, it will never send more than one operation in a batch because
          // each one has a different hash. We'll set an aggregate hash header via `customFetch` instead.
          // 'Toast-Persistent-Query-Hash':
          //   (operation.query as any)['__meta__']?.['hash'] ?? 'unknown',
          ...devHeaders
        }
      }))
      return forward(operation)
    }),
    createPersistedQueryLink({
      generateHash: (query) => {
        return (query as any)?.['__meta__']?.['hash'] ?? 'unknown'
      },
      useGETForHashedQueries: true,
      // Default behaviour: Disable the link on failed requests (i.e. if pqr is not supported yet)
      disable: isPersistedQueryError,
      retry: isPersistedQueryError,
      ...opts
    })
  ]

  return ApolloLink.from(links)
}

const isPersistedQueryError = (err: ErrorResponse) => {
  const isPqError =
    err.graphQLErrors?.some(
      ({ extensions, message }) =>
        extensions?.code === 'PERSISTED_QUERY_NOT_FOUND' ||
        extensions?.code === 'PERSISTED_QUERY_ERROR' ||
        message === 'PersistedQueryNotFound' ||
        message === 'PersistedQueryHeaderNotFound' ||
        message === 'PersistedQueryRetrievalError'
    ) ?? false

  if (isPqError) {
    console.group()
    console.warn('Persisted query error encountered', err)
    console.warn(
      'If you see this message in local development AND you changed a graphql operation, this just means that the persisted query is not yet available on the server. This is expected behaviour.\nIn this case, the next build will upload the new persisted query to the server and all should be good.'
    )
    console.warn(
      'If you see this message in (pre-)production, something is wrong with your persisted query setup.'
    )
    console.groupEnd()
  }

  return isPqError
}
